import React from 'react';

/**
 * AtlasIcon — the bespoke "Atlas, jet-age cut" mark set.
 *
 * One designed system of circle-and-line (Futura DNA) marks, NOT stock icons.
 * Every mark draws with `currentColor`, so a single component recolors itself
 * by mode and state (cream inactive → gold active in dark; navy/gilt in light;
 * black in mono) purely from the CSS `color` of its container.
 *
 * Geometry is transcribed verbatim from docs/design/brainstormy-visual-identity.md
 * §3 — do not redraw by eye.
 *
 * Usage:  <AtlasIcon name="projects" size={20} />
 *         <AtlasIcon name="orbital" size={26} title="Brainstormy" />
 */

// Each mark: its native viewBox + the inner geometry. Shared root attributes
// (fill/stroke/stroke-width) are carried on a wrapping <g> so currentColor wins.
const MARKS = {
  // ── Brand / structural ────────────────────────────────────────────────
  orbital: {
    viewBox: '0 0 26 26',
    body: (
      <>
        <ellipse cx="13" cy="13" rx="11.5" ry="4.6" fill="none" stroke="currentColor" strokeWidth="1.2" transform="rotate(-28 13 13)" />
        <circle cx="13" cy="13" r="4.3" fill="currentColor" />
        <circle cx="3.7" cy="8.6" r="1.5" fill="currentColor" />
      </>
    ),
  },
  starburst: {
    viewBox: '0 0 36 36',
    body: (
      <>
        <g fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
          <line x1="18" y1="2" x2="18" y2="34" />
          <line x1="2" y1="18" x2="34" y2="18" />
          <line x1="8.5" y1="8.5" x2="27.5" y2="27.5" />
          <line x1="27.5" y1="8.5" x2="8.5" y2="27.5" />
        </g>
        <circle cx="18" cy="18" r="2.4" fill="currentColor" />
      </>
    ),
  },
  spark: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.6">
        <line x1="12" y1="3" x2="12" y2="7" />
        <line x1="12" y1="17" x2="12" y2="21" />
        <line x1="3" y1="12" x2="7" y2="12" />
        <line x1="17" y1="12" x2="21" y2="12" />
        <circle cx="12" cy="12" r="3.2" />
      </g>
    ),
  },

  // ── Nav pictogram set ─────────────────────────────────────────────────
  projects: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="4" width="6.5" height="6.5" rx="1" />
        <rect x="13.5" y="4" width="6.5" height="6.5" rx="1" />
        <rect x="4" y="13.5" width="6.5" height="6.5" rx="1" />
        <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1" />
      </g>
    ),
  },
  sessions: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="4" width="18" height="12.5" rx="3" />
        <path d="M8 16.5 L8 20.5 L12 16.5" />
        <circle cx="8.5" cy="10.2" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="10.2" r="1" fill="currentColor" stroke="none" />
        <circle cx="15.5" cy="10.2" r="1" fill="currentColor" stroke="none" />
      </g>
    ),
  },
  outline: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <line x1="5.5" y1="4" x2="5.5" y2="20" />
        <circle cx="5.5" cy="6" r="1.7" fill="currentColor" stroke="none" />
        <circle cx="5.5" cy="12" r="1.7" fill="currentColor" stroke="none" />
        <circle cx="5.5" cy="18" r="1.7" fill="currentColor" stroke="none" />
        <line x1="9.5" y1="6" x2="20" y2="6" />
        <line x1="9.5" y1="12" x2="20" y2="12" />
        <line x1="9.5" y1="18" x2="20" y2="18" />
      </g>
    ),
  },
  // Corridor (Structure Workshop, U11) — a one-point-perspective passage: outer
  // frame, receding inner frame, four diagonals to the vanishing point. Same
  // circle-and-line DNA; distinct from `outline` (the legacy bullet spine).
  corridor: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
        <rect x="3.5" y="3.5" width="17" height="17" rx="1" />
        <rect x="9.5" y="9.5" width="5" height="5" rx="0.5" />
        <line x1="3.5" y1="3.5" x2="9.5" y2="9.5" />
        <line x1="20.5" y1="3.5" x2="14.5" y2="9.5" />
        <line x1="3.5" y1="20.5" x2="9.5" y2="14.5" />
        <line x1="20.5" y1="20.5" x2="14.5" y2="14.5" />
      </g>
    ),
  },
  // Story Board — a vertical spine of readiness nodes: the board's own
  // grammar (readiness is color, order is vertical) rather than a generic
  // grid/kanban glyph. Three states echo ReadinessDot: filled (ready), a
  // ring (needs-work), dashed (empty).
  board: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <line x1="12" y1="3" x2="12" y2="21" />
        <circle cx="12" cy="7" r="2" fill="currentColor" stroke="none" />
        <circle cx="12" cy="14" r="2" />
        <circle cx="12" cy="19.5" r="1.7" strokeDasharray="2.2 2.2" />
      </g>
    ),
  },
  bible: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 6 L5 5 L5 18 L12 19 Z" />
        <path d="M12 6 L19 5 L19 18 L12 19 Z" />
      </g>
    ),
  },
  // Front rect fill = current surface token, so the back rect is occluded.
  compendium: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="8" y="4" width="12" height="12" rx="2" />
        <rect x="4" y="8" width="12" height="12" rx="2" fill="var(--surface)" />
      </g>
    ),
  },
  research: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <line x1="9.5" y1="3.5" x2="14.5" y2="3.5" />
        <path d="M11 3.5 L11 9 L6 19 L18 19 L13 9 L13 3.5" />
        <line x1="8.4" y1="15" x2="15.6" y2="15" />
      </g>
    ),
  },
  // bookmark is not in spec §3 (which omits a mark for this destination);
  // designed here in the same circle-and-line DNA. Surfaced.
  bookmark: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
        <path d="M6 3.5 L18 3.5 L18 20.5 L12 16 L6 20.5 Z" />
      </g>
    ),
  },
  search: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <line x1="15.2" y1="15.2" x2="20" y2="20" />
      </g>
    ),
  },
  dashboard: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="3" />
        <circle cx="12" cy="3.5" r="1.5" fill="currentColor" stroke="none" />
      </g>
    ),
  },
  // Orbital + plus — new project / new session.
  new: {
    viewBox: '0 0 26 26',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.4">
        <ellipse cx="13" cy="13" rx="11.5" ry="4.6" transform="rotate(-28 13 13)" />
        <circle cx="13" cy="13" r="3.6" />
        <line x1="13" y1="10.6" x2="13" y2="15.4" />
        <line x1="10.6" y1="13" x2="15.4" y2="13" />
      </g>
    ),
  },
  // Genre badges (project cards, §3/§5.3): stack-of-books = series, single book
  // = novel. Geometry verbatim from the spec / docs/design/mocks/session-picker.html.
  series: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="4" y="15" width="16" height="4.5" rx="1" />
        <rect x="5.5" y="9.8" width="13" height="4.5" rx="1" />
        <rect x="7" y="4.6" width="10" height="4.5" rx="1" />
      </g>
    ),
  },
  novel: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="6.5" y="4" width="11" height="16" rx="1.2" />
        <line x1="9.5" y1="4" x2="9.5" y2="20" />
      </g>
    ),
  },
  // Timeline — the series corkboard: a horizontal line with pins
  // above (Entrance / Exit) and under (books / gaps). Same
  // circle-and-line DNA as the nav set. Not in identity spec §3;
  // added for the series-altitude left map (#683).
  timeline: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <line x1="3" y1="12" x2="21" y2="12" />
        <circle cx="8" cy="6.5" r="1.7" />
        <circle cx="7" cy="17.5" r="1.7" fill="currentColor" stroke="none" />
        <circle cx="15.5" cy="17.5" r="1.7" />
      </g>
    ),
  },
  // Notes — lined page. Same circle-and-line DNA as the nav set.
  // Not in identity spec §3; added for the project Notes room (#672).
  notes: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M7 20.5 L17 20.5 L17 4.5 L7 4.5 Z" />
        <line x1="9.5" y1="9" x2="14.5" y2="9" />
        <line x1="9.5" y1="12.5" x2="14.5" y2="12.5" />
        <line x1="9.5" y1="16" x2="13" y2="16" />
      </g>
    ),
  },
  // Upload — page + upward arrow. Same circle-and-line DNA as the nav set.
  // Not in identity spec §3; added for Document Import v1 (#627).
  upload: {
    viewBox: '0 0 24 24',
    body: (
      <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M7 20.5 L17 20.5 L17 10 L12 4.5 L7 10 Z" />
        <line x1="12" y1="16.5" x2="12" y2="9.5" />
        <path d="M9.4 11.6 L12 9 L14.6 11.6" />
      </g>
    ),
  },
};

export const ATLAS_ICON_NAMES = Object.keys(MARKS);

function AtlasIcon({ name, size = 20, className, title, ...rest }) {
  const mark = MARKS[name];
  if (!mark) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`AtlasIcon: unknown mark "${name}"`);
    }
    return null;
  }
  const labelled = Boolean(title);
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={mark.viewBox}
      role={labelled ? 'img' : 'presentation'}
      aria-label={labelled ? title : undefined}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
      {...rest}
    >
      {labelled ? <title>{title}</title> : null}
      {mark.body}
    </svg>
  );
}

export default AtlasIcon;
